/**
 * SuperJarvis Web Application - Main Router
 * 
 * Security: All routes except HomeScreen require authentication.
 * Users must log in before accessing any application features.
 * 
 * Routes:
 * - / (public) - Landing page with login
 * - /chat (protected) - AI conversation interface
 * - /tasks (protected) - Task management
 * - /apps (protected) - App launcher
 * - /projects (protected) - Project management
 * - /recent (protected) - Recent activity
 */
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { GhostProvider, useGhost } from './contexts/GhostContext';
import { TasksProvider } from './contexts/TasksContext';
import { GhostSpotlight } from './components/shared';
import { ErrorBoundary } from './components/ErrorBoundary';
import HomeScreen from './screens/HomeScreen';
import AuthCallbackScreen from './screens/AuthCallbackScreen';
import ChatScreen from './screens/ChatScreen';
import MyTasksScreen from './screens/MyTasksScreen';
import MyAppsScreen from './screens/MyAppsScreen';
import ProjectsScreen from './screens/ProjectsScreen';
import RecentScreen from './screens/RecentScreen';
import CreateAppScreen from './screens/CreateAppScreen';
import AppRunnerScreen from './screens/AppRunnerScreen';
import MonitorScreen from './screens/MonitorScreen';
import PrivacyScreen from './screens/PrivacyScreen';
import TermsScreen from './screens/TermsScreen';
import IntegrationsScreen from './screens/IntegrationsScreen';
import SettingsScreen from './screens/SettingsScreen';
import LiveModeScreen from './screens/LiveModeScreen';
// OnboardingScreen removed — users go straight to HomeScreen

/**
 * ProtectedRoute - Security wrapper for authenticated routes
 * 
 * Redirects unauthenticated users to the home page (login).
 * Shows a loading state while authentication is being verified.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthContext();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0a0a0f 100%)',
        color: '#ffffff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(139, 92, 246, 0.3)',
            borderTopColor: '#8b5cf6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ opacity: 0.7 }}>Loading SuperJarvis...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Redirect to home (login) if not authenticated
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}

/**
 * GlobalGhostSpotlight - System-wide Ghost Mode overlay
 * 
 * Responds to the global hotkey (Cmd/Ctrl + Shift + Space) and
 * allows users to quickly send messages from anywhere in the app.
 */
function GlobalGhostSpotlight() {
  const { isGhostOpen, closeGhost } = useGhost();
  const navigate = useNavigate();

  const handleSubmit = (text: string) => {
    // Navigate to chat with the message
    navigate(`/?message=${encodeURIComponent(text)}`);
  };

  return (
    <GhostSpotlight
      isOpen={isGhostOpen}
      onClose={closeGhost}
      onSubmit={handleSubmit}
    />
  );
}

/**
 * App - Main application component
 * 
 * Provides authentication context, ghost mode context, and routing.
 * All routes except the home page are protected and require login.
 * Wrapped in ErrorBoundary to prevent black screens on render errors.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <TasksProvider>
          <GhostProvider>
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<HomeScreen />} />
                {/* Onboarding removed — redirect to home */}
                <Route path="/onboarding" element={<Navigate to="/" replace />} />
                <Route path="/auth/callback" element={<AuthCallbackScreen />} />
                <Route path="/privacy" element={<PrivacyScreen />} />
                <Route path="/terms" element={<TermsScreen />} />

                {/* Protected routes - Require authentication */}
                <Route path="/chat" element={
                  <ProtectedRoute>
                    <ChatScreen />
                  </ProtectedRoute>
                } />
                <Route path="/tasks" element={
                  <ProtectedRoute>
                    <MyTasksScreen />
                  </ProtectedRoute>
                } />
                <Route path="/apps" element={
                  <ProtectedRoute>
                    <MyAppsScreen />
                  </ProtectedRoute>
                } />
                <Route path="/apps/create" element={
                  <ProtectedRoute>
                    <CreateAppScreen />
                  </ProtectedRoute>
                } />
                <Route path="/apps/:appId" element={
                  <ProtectedRoute>
                    <AppRunnerScreen />
                  </ProtectedRoute>
                } />
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <ProjectsScreen />
                  </ProtectedRoute>
                } />
                <Route path="/recent" element={
                  <ProtectedRoute>
                    <RecentScreen />
                  </ProtectedRoute>
                } />
                <Route path="/monitor" element={
                  <ProtectedRoute>
                    <MonitorScreen />
                  </ProtectedRoute>
                } />

                {/* Integrations routes */}
                <Route path="/integrations" element={
                  <ProtectedRoute>
                    <IntegrationsScreen />
                  </ProtectedRoute>
                } />
                <Route path="/integrations/:type" element={
                  <ProtectedRoute>
                    <IntegrationsScreen />
                  </ProtectedRoute>
                } />

                {/* Settings routes */}
                <Route path="/settings/:type" element={
                  <ProtectedRoute>
                    <SettingsScreen />
                  </ProtectedRoute>
                } />

                {/* Live Mode */}
                <Route path="/live" element={
                  <ProtectedRoute>
                    <LiveModeScreen />
                  </ProtectedRoute>
                } />

                {/* Catch-all redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <GlobalGhostSpotlight />
            </BrowserRouter>
          </GhostProvider>
        </TasksProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
